import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { finalize, of, Subject, takeUntil } from 'rxjs';
import { WorkOrderService } from '../../../../services/workorder.service';
import { Store } from '@ngxs/store';
import { FinishedProductInformation } from '../../model/finished-product-information.model';
import { ActivatedRoute } from '@angular/router';
import { DealerState } from '../../../../../../shared/state/DealerState';
import { TableConfig } from '@michelin/theme';
import { MatTableDataSource } from '@angular/material/table';
import { FinishedProductModel } from '../../model/finished-product.model';
import { RawMaterialDTO } from '../../model/raw-materials.model';
import { TranslationService } from '../../../../../../shared/translations/services/translation.service';
import { TranslationState } from '../../../../../../shared/state/TranslationState';

@Component({
  selector: 'app-finished-product-tab',
  templateUrl: './finished-product-tab.component.html',
  styleUrls: ['./finished-product-tab.component.scss'],
  standalone: false
})
export class FinishedProductTabComponent implements OnInit, OnDestroy {
  @Input() workOrderLineId: string | null = null;
  @Input() casingStatus: string | null = null;
  @Output() dataChanged = new EventEmitter<any>();
  @Output() hasChanges = new EventEmitter<boolean>();

  private static readonly RELEASED_STATUS = 'RELEASED';

  public formBuilder = inject(FormBuilder);
  public workOrderService = inject(WorkOrderService);
  public store = inject(Store);
  public route = inject(ActivatedRoute);
  public translationService = inject(TranslationService);
  private cdr = inject(ChangeDetectorRef);

  rejectedCasingForm: FormGroup;
  private destroy$ = new Subject<void>();
  finishedProductInformation: FinishedProductInformation;
  dealerId: string;
  workOrderId: string;
  loading = false;

  finishedProductConfig: TableConfig<FinishedProductModel> = {
    columns: {
      mspn: {
        cell: (item: any) => item.mspn,
        header: this.translationService.translate('mspnText')
      },
      dealerFinishedRetreadNumber: {
        cell: (item: any) => item.dealerRetreadNo,
        header: this.translationService.translate('dealerFinishedRetreadNumberText')
      },
      capNumber: {
        cell: (item: any) => item.dealerCapNo,
        header: this.translationService.translate('capNumberText')
      },
      casingNumber: {
        cell: (item: any) => item.dealerCasingNo,
        header: this.translationService.translate('casingNumberText')
      },
      capAndCasingNumber: {
        cell: (item: any) => item.dealerCapAndCasingNo,
        header: this.translationService.translate('capCasingText')
      },
      adFinishedRetreadNumber: {
        cell: (item: any) => item.dealerAdRetreadNo,
        header: this.translationService.translate('adFinishedRetreadNumberText')
      },
      adCapNumber: {
        cell: (item: any) => item.dealerAdCapNo,
        header: this.translationService.translate('adCapNumberText')
      },
      adCCCasingNumber: {
        cell: (item: any) => item.dealerAdCasingNo,
        header: this.translationService.translate('adCcCasingNumberText')
      }
    }
  };

  finishedProductDataSource = new MatTableDataSource<FinishedProductModel>();

  tableConfig: TableConfig<RawMaterialDTO> = {
    columns: {
      tread: { cell: (elm: RawMaterialDTO) => elm.tread ?? '', header: this.translationService.translate('treadText') },
      treadPartNumber: {
        cell: (elm: RawMaterialDTO) => elm.treadPartNumber ?? '',
        header: this.translationService.translate('treadPartNumberText')
      },
      treadWeight: {
        cell: (elm: RawMaterialDTO) => elm.treadWeight?.toString() ?? '',
        header: this.translationService.translate('treadWeightText')
      },
      ctcPartNumber: {
        cell: (elm: RawMaterialDTO) => elm.ctcPartNumber ?? '',
        header: this.translationService.translate('ctcPartNumberText')
      },
      ctcWeight: {
        cell: (elm: RawMaterialDTO) => elm.ctcWeight?.toString() ?? '',
        header: this.translationService.translate('wolCtcWeightText')
      },
      wastedMaterial: {
        cell: (elm: RawMaterialDTO) => elm.wastedMaterial ?? '',
        header: this.translationService.translate('wastedMaterialText')
      },
      wastedReason: {
        cell: (elm: RawMaterialDTO) => elm.wastedReason ?? '',
        header: this.translationService.translate('WastedReasonText')
      }
    }
  };

  rawMaterialsDataSource = new MatTableDataSource<RawMaterialDTO>([]);

  constructor() {
    this.dealerId = this.store.selectSnapshot(DealerState.currentDealer)?.id;
    this.workOrderId = this.route.snapshot.paramMap.get('workOrderId');
    this.rejectedCasingForm = this.formBuilder.group({
      rejectCode: [''],
      rejectDate: [''],
      rejectPost: [''],
      rejectDescription: [''],
      rejectUser: [''],
      disposition: [''],
      disposalCharge: [''],
      disposalChargeDescription: [''],
      disposalChargeRMPartNo: [''],
      disposalChargeFGPartNo: [''],
      rejectPartNo: ['']
    });
  }

  ngOnInit(): void {
    this.setupFormValueChanges();

    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Update finished product table headers
        this.finishedProductConfig.columns.mspn.header = this.translationService.translate('mspnText');
        this.finishedProductConfig.columns.dealerFinishedRetreadNumber.header =
          this.translationService.translate('dealerFinishedRetreadNumberText');
        this.finishedProductConfig.columns.capNumber.header = this.translationService.translate('capNumberText');
        this.finishedProductConfig.columns.casingNumber.header = this.translationService.translate('casingNumberText');
        this.finishedProductConfig.columns.capAndCasingNumber.header = this.translationService.translate('capCasingText');
        this.finishedProductConfig.columns.adFinishedRetreadNumber.header =
          this.translationService.translate('adFinishedRetreadNumberText');
        this.finishedProductConfig.columns.adCapNumber.header = this.translationService.translate('adCapNumberText');
        this.finishedProductConfig.columns.adCCCasingNumber.header = this.translationService.translate('adCcCasingNumberText');

        // Update raw materials table headers
        this.tableConfig.columns.tread.header = this.translationService.translate('treadText');
        this.tableConfig.columns.treadPartNumber.header = this.translationService.translate('treadPartNumberText');
        this.tableConfig.columns.treadWeight.header = this.translationService.translate('treadWeightText');
        this.tableConfig.columns.ctcPartNumber.header = this.translationService.translate('ctcPartNumberText');
        this.tableConfig.columns.ctcWeight.header = this.translationService.translate('wolCtcWeightText');
        this.tableConfig.columns.wastedMaterial.header = this.translationService.translate('wastedMaterialText');
        this.tableConfig.columns.wastedReason.header = this.translationService.translate('WastedReasonText');

        // Reassign to trigger table refresh in OnPush child components.
        this.finishedProductConfig = { ...this.finishedProductConfig };
        this.tableConfig = { ...this.tableConfig };
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFormValueChanges(): void {
    const forms = [this.rejectedCasingForm];

    forms.forEach((form) => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.emitDataChanged();
        this.hasChanges.emit(this.hasFormChanges());
      });
    });
  }

  private loadFinishedProductData(): void {
    // Use cached payload when available, otherwise fetch from API.
    const finishedProduct$ = this.finishedProductInformation
      ? of(this.finishedProductInformation)
      : this.workOrderService.getFinishedProductInformation(this.dealerId, this.workOrderId, this.workOrderLineId);

    finishedProduct$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (data) => {
          const model = this.mapToFinishedInformationModel(data);
          this.rejectedCasingForm.patchValue(model.rejectedCasingInformation, { emitEvent: false });

          // Replace references so mic-table can refresh immediately.
          // Released casings have not yet gone through finishing production, so any
          // finishedProductDTO returned for them is placeholder data and must be suppressed.
          const isReleasedCasing = this.casingStatus === FinishedProductTabComponent.RELEASED_STATUS;
          this.finishedProductDataSource = new MatTableDataSource<FinishedProductModel>(
            data?.finishedProductDTO && !isReleasedCasing ? [data.finishedProductDTO] : []
          );
          this.rawMaterialsDataSource = new MatTableDataSource<RawMaterialDTO>(model.rawMaterials ?? []);
          this.finishedProductInformation = data;
          this.cdr.detectChanges();
        },
        error: () => {
          this.finishedProductDataSource = new MatTableDataSource<FinishedProductModel>([]);
          this.rawMaterialsDataSource = new MatTableDataSource<RawMaterialDTO>([]);
        }
      });
  }

  private hasFormChanges(): boolean {
    return this.rejectedCasingForm.dirty;
  }

  private emitDataChanged(): void {
    const formData = {
      rejectedCasing: this.rejectedCasingForm.value
    };
    this.dataChanged.emit(formData);
  }

  public resetForms(): void {
    this.rejectedCasingForm.markAsPristine();
  }

  public loadData(): void {
    this.loading = true;
    this.loadFinishedProductData();
  }

  mapToFinishedInformationModel = (apiResponse: any) => ({
    rejectedCasingInformation: {
      rejectCode: apiResponse.rejectedCasingInformationDTO.rejectReason,
      rejectDate: apiResponse.rejectedCasingInformationDTO.rejectDate,
      rejectPost: apiResponse.rejectedCasingInformationDTO.rejectPostName,
      rejectDescription: apiResponse.rejectedCasingInformationDTO.rejectReasonDescription,
      rejectUser: apiResponse.rejectedCasingInformationDTO.rejectUser,
      disposition: apiResponse.rejectedCasingInformationDTO.disposition,
      disposalCharge: apiResponse.rejectedCasingInformationDTO.disposalCharge,
      disposalChargeDescription: apiResponse.rejectedCasingInformationDTO.disposalChargeDescription,
      disposalChargeRMPartNo: apiResponse.rejectedCasingInformationDTO.disposalChargeRMPartNumber,
      disposalChargeFGPartNo: apiResponse.rejectedCasingInformationDTO.disposalChargeFGPartNumber,
      rejectPartNo: apiResponse.rejectedCasingInformationDTO.rejectPartNumber
    },
    rawMaterials: apiResponse.rawMaterialsDTO
  });
}
